






































import {computed, defineComponent, onBeforeUnmount, onMounted, reactive, Ref, ref, watch} from "@vue/composition-api"
import Timer from "../../../misc/Timer.vue"
import io from 'socket.io-client'
import {useToast} from "vue-toastification/composition"
import {MemoryGame} from "@/games/flashcards/memory-game/memory-cards"
import {gsap, Power2} from "gsap"
import Card from "@/games/flashcards/memory-game/Card.vue"
import Leaderboard from "@/games/Leaderboard.vue"
import {MemoryGameState} from "@/games/flashcards/memory-game/memory-game-types-server"
import {CardInterface} from "@/games/flashcards/memory-game/card-interface"
import { IOInfo } from '@/store'

const flipDuration = 0.3

type UIState = InfoUIState
  | StartingUIState
  | ShowingUIState
  | PlayingUIState
  | WaitingUIState
  | FinishedRoundUIState
  | AllFinishedRoundUIState
  | FinishedGameUIState
interface InfoUIState {
  type: 'info';
  info?: {header: string; body: string[]};
}
interface StartingUIState { type: 'starting' }
interface ShowingUIState { type: 'showing' }
interface PlayingUIState { type: 'playing' }
interface WaitingUIState { type: 'waiting' }
interface FinishedRoundUIState { type: 'finished-round'}
interface AllFinishedRoundUIState { type: 'all-finished-round'}
interface FinishedGameUIState { type: 'finished-game'}

const roundInfo = {
  1: {
    header: 'Round 1',
    body: [
      'In this round you start with 60 points and get penalised for each mistake you make',
      'Stay calm and take your time...'
    ]
  },
  2: {
    header: 'Round 2',
    body: ['Finish as fast as you can for extra points']
  },
  3: {
    header: 'Round 3',
    body: [
      'Race to the finish line!',
      'First one to complete this larger board wins the round'
    ]
  }
}

export class CaseNotExhaustiveError extends Error {
  constructor(val: never) {
    super(`Unreachable case: ${val}`)
  }
}

export default defineComponent({
  name: "MemoryGame",
  components: {Card, Timer, Leaderboard},
  props: {
    ioInfo: {
      type: Object as () => IOInfo,
      required: true
    },
    windowOrientation: {
      type: String,
      required: true
    },
  },
  setup(props, { emit, root }) {
    const toast = useToast()

    const componentRefs = cRefs()
    const game = ref<MemoryGame>()
    const uiState = ref<UIState>({type: 'starting'})
    const state = ref<MemoryGameState>()
    const timer = reactive({
      started: false,
      timeLeft: 90,
    })

    onMounted(() => {
      props.ioInfo.socket.emit('memory game : request state', { roomId: props.ioInfo.roomId })
    })


    const numCards = computed(() => game.value!.cards.length)
    const nicks = computed(() => root.$store.state.nicks as Record<string, string>)
    const stats = computed(() => {
      return Object.entries(state.value!.scores)
        .map(([k, v]) => ({name: nicks.value[k], score: v}))
        .sort((a, b) => b.score - a.score)
    })

    const playersToFinish = computed(() => {
      return Object.keys(state.value!.scores).length - state.value!.playersFinished.length
    })

    cSocket(props.ioInfo, state, uiState, timer)

    watch(uiState, (newVal, oldVal) => {
      if (newVal === undefined)
        throw 'Never assign undefined to uiState'
      console.log(`UI state: ${newVal.type}   Old UI state: ${oldVal?.type}`)
      switch (newVal.type) {
        case "starting":
          console.log('Waiting for game state...')
          break
        case "info":
          if (oldVal.type === 'starting') {
            console.log('Starting game')
            game.value = new MemoryGame(state.value!.cards)
          }
          root.$nextTick(() => {
            const tl = gsap.timeline()
            tl.to(componentRefs.intro.value!, 1, {css: {opacity: 1, display: 'block'}, ease: Power2.easeOut})
            tl.to({}, 3.5, {})
            tl.to(componentRefs.intro.value!, 1, {css: {opacity: 0, display: 'none'}, ease: Power2.easeOut})
            const gameFinished = newVal.info === undefined
            tl.eventCallback("onComplete", () => {
              if (gameFinished) {
                const winner = stats.value[0].name
                if (winner === 'You') {
                  toast.success('Congratulations, you won!!!')
                } else {
                  toast.warning(`Player ${winner} won!!!`)
                }
                emit('end')
              } else {
                uiState.value = {type: "showing"}
              }
            })
          })
          break
        case "showing":
          root.$nextTick(() => {
            const duration = 0.6
            const stagger = 0.1
            const totalTime = (numCards.value - 1) * stagger
            const startBefore = ((numCards.value - 1)/numCards.value) * totalTime
            const tl = gsap.timeline()

            tl.to(componentRefs.cardElements.value!.map(x => x.inner), duration, {css:{rotationY:"+=180"}, ease: Power2.easeInOut, stagger: stagger})
            tl.to(componentRefs.cardElements.value!.map(x => x.inner), duration, {css:{rotationY:"+=180"}, ease: Power2.easeInOut, stagger: stagger}, `-=${startBefore}`)
            tl.to(componentRefs.cardElements.value!.map(x => x.inner), duration, {css:{rotationY:"+=180"}, ease: Power2.easeInOut, stagger: stagger}, `-=${startBefore}`)
            tl.to(componentRefs.cardElements.value!.map(x => x.inner), duration, {css:{rotationY:"+=180"}, ease: Power2.easeInOut, stagger: stagger}, `-=${startBefore}`)
            tl.eventCallback("onComplete", () => {
              uiState.value = {type: "playing"}
            })
          })
          break
        case "playing":
          timer.started = true
          break
        case "finished-round":
          timer.started = false
          {
            const tl = gsap.timeline()
            tl.to('.flip-card-inner', 3, {css: {translateY: "+=900"}, ease: Power2.easeInOut, stagger: 0.1})
            tl.eventCallback('onComplete', () => {
              if (uiState.value?.type === 'finished-round' && (state.value!.round === 1 || state.value!.round === 2)) {
                uiState.value = {type: "waiting"}
              } else if (uiState.value?.type === 'finished-game') {
                uiState.value = {type: "info"}
              } else {
                cacheImages(state.value!.cards.map(card => card.back))
                game.value = new MemoryGame(state.value!.cards)
                uiState.value = {
                  type: "info",
                  info: roundInfo[state.value!.round],
                }
              }
            })
          }
          break
        case "waiting":
          break
        case "all-finished-round":
          if (oldVal?.type === "waiting" || oldVal.type === "playing") {
            game.value = new MemoryGame(state.value!.cards)
            uiState.value = {
              type: "info",
              info: roundInfo[state.value!.round],
            }
          } else if (oldVal?.type !== "finished-round") {
            throw `Finished round while in ${oldVal?.type} state`
          }
          break
        case "finished-game":
          if (oldVal?.type !== 'finished-round') {    // Already animated
            root.$nextTick(() => {
              const tl = gsap.timeline()
              tl.to('.flip-card-inner', 3, {css: {translateY: "+=900"}, ease: Power2.easeInOut, stagger: 0.1})
              tl.eventCallback('onComplete', () => {
                uiState.value = {type: "info"}
              })
            })
          }
          break
        default:
          throw new CaseNotExhaustiveError(newVal)
      }
    })

    function onFlashcardClick(evt: {identifier: number}) {
      if (!(uiState.value!.type === 'playing')) {
        console.log('Not playing yet')
        return
      }

      selectCard(game, evt.identifier, componentRefs.cardElements.value!, componentRefs.flipSound.value!, props.ioInfo)

      if (game.value!.allRevealed) {
        uiState.value = {type: "finished-round"}
        props.ioInfo.socket.emit(
          'memory game : won',
          {
            roomId: props.ioInfo.roomId,
            playerId: props.ioInfo.peerId,
            remainingTime: timer.timeLeft
          })
      }
    }

    function onTimesUp() {
      console.log('Time\'s up!')
      props.ioInfo.socket.emit(
        'memory game : timeout',
        {
          roomId: props.ioInfo.roomId,
          playerId: props.ioInfo.peerId,
          remainingTime: timer.timeLeft
        })
    }

    return {
      game,
      state,
      uiState,
      stats,
      timer,
      flipDuration,
      ...cStyles(
        numCards,
        computed(() => root.$store.state.windowOrientation as string)
      ),
      playersToFinish,
      onFlashcardClick,
      onTimesUp,
      ...componentRefs,
    }
  }
})

function cSocket(
  ioInfo: IOInfo,
  state: Ref<MemoryGameState | undefined>,
  uiState: Ref<UIState>,
  timer: {started: boolean; timeLeft: number},
) {
  ioInfo.socket.on('memory game : state', (payload: MemoryGameState) => {
    console.log('Received state')
    state.value = payload
    if (uiState.value.type === 'starting') {
      cacheImages(state.value.cards.map(card => card.back))
      uiState.value = {
        type: 'info',
        info: roundInfo["1"]
      }
    }
  })

  ioInfo.socket.on('memory game : finished-round', (payload: MemoryGameState) => {
    console.log('Finished round 1')
    timer.timeLeft = {
      1: 90,
      2: 60,
      3: 150,
    }[payload.round]
    state.value = payload
    uiState.value = {type: 'all-finished-round'}
  })

  ioInfo.socket.on('memory game : finished', () => {
    console.log('Finished round 3')
    uiState.value = {type: 'finished-game'}
  })

  onBeforeUnmount(() => {
    ioInfo.socket.off('memory game : state')
    ioInfo.socket.off('memory game : finished-round')
    ioInfo.socket.off('memory game : finished')
  })
}


function cRefs() {
  return {
    intro: ref<HTMLElement>(),
    flipSound: ref<HTMLAudioElement>(),
    cardElements: ref<CardInterface[]>(),
  }
}

function cStyles(numCards: Ref<number>, windowOrientation: Ref<string>) {
  return {
    gameGridStyle: computed(() => ({
      display: 'grid',
      gridColumn: 1,
      gridRow: windowOrientation.value === 'landscape' ? 1 : 2,
      gridTemplateColumns: `repeat(${Math.sqrt(numCards.value)}, 1fr)`,
      gridTemplateRows: `repeat(${Math.sqrt(numCards.value)}, 1fr)`,
      gridGap: '1rem',
    }))
  }
}

function cacheImages(images: string[]) {
  images.forEach(src => {
    const image = new Image()
    image.src = src
  })
}

function selectCard(
  game: Ref<MemoryGame | undefined>,
  selectedCard: number,
  cards: CardInterface[],
  flipSound: HTMLAudioElement,
  ioInfo: IOInfo,
) {
  const action = game.value!.select(selectedCard)

  switch (action.type) {
    case "selected-already-paired":
      console.log('Selected already paired')
      cards[action.card].pairedAnimation()
      break
    case "selected-new":
      console.log('Selected new')
      cards[action.card].flip()
      break
    case "selected-pair":
      console.log('Selected pair')
      {
        const tl = gsap.timeline()
        cards[action.card].addFlipToTimeline(tl)
        tl.add(() => flipSound.play())
        cards[action.pair].addPairedAnimationToTimeline(tl, flipDuration)
        cards[action.card].addPairedAnimationToTimeline(tl, flipDuration)
        tl.eventCallback("onComplete", () => {
          cards[action.card].animating = false
          cards[action.pair].animating = false
        })
      }
      ioInfo.socket.emit('memory game : pair', {roomId: ioInfo.roomId, playerId: ioInfo.peerId})
      break
    case "unselected-card":
      console.log('Unselected card')
      cards[action.card].flip()
      ioInfo.socket.emit('memory game : unselected', {roomId: ioInfo.roomId, playerId: ioInfo.peerId})
      break
    case "wrong-pair":
      console.log('Wrong pair')
      {
        const tl = gsap.timeline()
        cards[action.card].addFlipToTimeline(tl)
        cards[action.pair].addFlipToTimeline(tl, flipDuration)
        cards[action.card].addFlipToTimeline(tl, flipDuration)
        tl.eventCallback("onComplete", () => {
          cards[action.card].animating = false
          cards[action.pair].animating = false
        })
      }
      ioInfo.socket.emit('memory game : incorrect', {roomId: ioInfo.roomId, playerId: ioInfo.peerId})
      break
    default:
      throw new CaseNotExhaustiveError(action)
  }
}

